import AuraAttribute, {
  AuraAttributeAnswer,
  PreferenceQuestionAnswer,
  PreferenceQuestionAnswerFormValues,
  PreferenceQuestionView,
  SublistAnswer,
  SublistQuestionAnswers,
} from 'store/types/AuraAttribute';
import { getExplanationFieldName, getQuestionFieldName, getQuestionId } from './Application';
import QuestionType from 'store/enums/QuestionType';
import React from 'react';
import TextQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/TextQuestionFormItem';
import CheckboxQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/CheckboxQuestionFormItem';
import RadioQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/RadioQuestionFormItem';
import DropdownQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/DropdownQuestionFormItem';
import SublistQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/SublistQuestionFormItem';
import MultilineTextQuestionFormItem from 'components/profile/EditPreferencesPage/EditPreferencesForm/MultilineTextQuestionFormItem';

export const PREFERENCES_FIELD_NAME = 'preferences';

export const getPreferencesAnswersData = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  formValues: any,
  questionsList: PreferenceQuestionView[] = []
): PreferenceQuestionAnswer[] => {
  const result: PreferenceQuestionAnswer[] = [];
  Object.keys(formValues).forEach((key: string) => {
    const questionId: string = getQuestionId(key);

    if (questionId) {
      const formValue = formValues[key];
      const explanationFormValue: string | undefined = formValues[getExplanationFieldName(key)];
      const question = questionsList.find(({ id }) => id === questionId);
      const questionType: QuestionType = question?.questionType || QuestionType.Text;
      const answerName = question?.options?.find(({ id }) => id === formValue)?.option;
      switch (questionType) {
        case QuestionType.Text:
        case QuestionType.MultilineText:
          result.push({ attributeId: questionId, answer: formValue });
          break;
        case QuestionType.Checkbox:
          result.push({
            attributeId: questionId,
            selections: (formValue || [])
              .filter(({ value }: any) => !!value)
              .map(({ id, explanation, name }: any) => ({ value: id, explanation, name })),
          });
          break;
        case QuestionType.SubList:
          result.push({ attributeId: questionId, sublistAnswers: formValue });
          break;
        case QuestionType.Dropdown:
        case QuestionType.Radio:
          result.push({
            attributeId: questionId,
            selections: formValue ? [{ value: formValue, explanation: explanationFormValue, name: answerName }] : [],
          });
          break;
      }
    }
  });
  return result;
};

export const getQuestionInput = (question: PreferenceQuestionView, disabled?: boolean): React.ReactNode => {
  const inputProps = { question, disabled };

  switch (question.questionType) {
    case QuestionType.Text:
      return <TextQuestionFormItem {...inputProps} />;
    case QuestionType.MultilineText:
      return <MultilineTextQuestionFormItem {...inputProps} />;
    case QuestionType.Checkbox:
      return <CheckboxQuestionFormItem {...inputProps} />;
    case QuestionType.Radio:
      return <RadioQuestionFormItem {...inputProps} />;
    case QuestionType.Dropdown:
      return <DropdownQuestionFormItem {...inputProps} />;
    case QuestionType.SubList:
      return <SublistQuestionFormItem {...inputProps} />;
    default:
      return <></>;
  }
};

const getSublistAnswerValues = (
  questions: SublistQuestionAnswers,
  answers: AuraAttributeAnswer[]
): Array<SublistAnswer[]> => {
  const result: Array<SublistAnswer[]> = [];

  Object.entries(questions || []).forEach(([referenceKey, questions]) => {
    questions?.forEach(({ answer, id: sublistQuestionId, type, name, options }) => {
      const foundIndex = result.findIndex(
        (answerArray) => !!answerArray.find((answer) => answer.referenceKey === referenceKey)
      );
      const answerId =
        answers.find((a) => a.sublistQuestionId === sublistQuestionId && a.sublistAnswerGroup === referenceKey)?.id ??
        '0';
      const answerValue =
        type.name === QuestionType.Dropdown ||
        type.name === QuestionType.Radio ||
        type.name === QuestionType.DropdownCustomList
          ? options.find((o) => o.selected)?.id
          : answer;

      const newValue = {
        referenceKey,
        answer: answerValue ?? '',
        answerId,
        type: type.name as QuestionType,
        sublistQuestionId,
        name,
      };

      if (foundIndex !== -1) {
        result[foundIndex].push(newValue);
      } else {
        result.push([newValue]);
      }
    });
  });

  return result;
};

export const getDefaultPreferencesFormValues = (
  questions: PreferenceQuestionView[] = [],
  includeAnswers = true
): PreferenceQuestionAnswerFormValues => {
  let result = {};

  questions.forEach(({ questionType, id, options = [], answer, sublistQuestionAnswers, answers }) => {
    const fieldName = getQuestionFieldName(id);

    switch (questionType) {
      case QuestionType.Checkbox:
        result = {
          ...result,
          [fieldName]: options.map(
            ({ id, option: name, selected, requiresDetail: includeExplanation, detail: explanation }) => ({
              id,
              name,
              includeExplanation,
              explanation,
              value: includeAnswers ? selected : false,
            })
          ),
        };
        break;
      case QuestionType.SubList:
        result = {
          ...result,
          [fieldName]: getSublistAnswerValues(sublistQuestionAnswers ?? {}, answers ?? []),
        };
        break;
      case QuestionType.Text:
      case QuestionType.MultilineText:
      case QuestionType.Radio:
        result = { ...result, [fieldName]: includeAnswers ? answer || '' : '' };
        break;
      case QuestionType.Dropdown:
        result = {
          ...result,
          [fieldName]: includeAnswers ? options.find(({ selected: isSelected }) => isSelected)?.id || '' : '',
        };
        break;
    }
  });
  return result;
};

const getQuestionType = (name: string): QuestionType | undefined => {
  if (Object.values(QuestionType).includes(name as QuestionType)) {
    return name as QuestionType;
  }
  return undefined;
};

export const getPreferencesQuestionsView = (
  questions: AuraAttribute[] = [],
  fieldName = ''
): PreferenceQuestionView[] =>
  (questions || []).map((item) => ({
    ...item,
    fieldName: `${fieldName ? `${fieldName}.` : ''}${getQuestionFieldName(item.id)}`,
    options: item.options || [],
    sublistQuestions: item.sublistQuestions || [],
    questionType: getQuestionType(item.dataCollectionType.name),
  }));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isSubListQuestionsValid = (data: PreferenceQuestionView[] = [], values: any): boolean =>
  data
    .filter(({ required, dataCollectionType: type }) => required && type.name === QuestionType.SubList)
    .map(({ fieldName }) => fieldName)
    .every(
      (questionId) => !!questionId.split('.').reduce((nextObject: any, key: string) => nextObject[key], values).length
    );
